import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { LuArrowLeft, LuMenu } from 'react-icons/lu';

import {
  MobileHeaderLayout,
  PAGE_SCAFFOLD_DEFAULT_MOBILE_TOP_OFFSET,
} from '../../constants/pageScaffold';
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport';
import { useSidebarShellStore } from '../../store/sidebarShell';
import { MobileTopGradient } from '../MobileTopGradient/MobileTopGradient';
import { ReturnButton } from '../ReturnButton/ReturnButton';

export interface PageScaffoldProps {
  children: ReactNode;
  pageName?: string;
  pageDescription?: string;
  returnPath?: string;
  mobileReturnPath?: string;
  mobileHeaderLayout?: MobileHeaderLayout;
  noPadding?: boolean;
  disableScroll?: boolean;
  noChildrenSpacing?: boolean;
  hideScrollbar?: boolean;
  containerClassName?: string;
  containerPaddingClassName?: string;
  ownsScrollViewport?: boolean;
  mobileTopOffset?: number;
  headerAction?: ReactNode;
  mobileDescriptionSlot?: ReactNode;
  mobileTrailingSlot?: ReactNode;
}

const stylesheet = {
  pageScaffoldWrapper: 'w-full',
  pageScaffoldWrapperNoScroll: 'w-full h-full',
  containerBase: 'flex flex-col w-full h-full bg-semantic-bg-page! gap-0!',
  containerScrollModifiers:
    'overflow-y-auto min-h-screen justify-center justify-start',
  containerPaddingModifiers: 'md:px-1 md:mt-0',
  containerHideScrollbar: 'scrollbar-hide',
  childrenWrapper: 'mt-4',
  childrenWrapperNoScroll: 'md:mt-4 flex-1 flex flex-col overflow-hidden',
  childrenWrapperNoScrollNoSpacing: 'flex-1 flex flex-col overflow-hidden',
  childrenWrapperFullHeight: 'h-full',
  showHeaderWrapper: 'flex flex-col justify-center gap-0',
  header: {
    container:
      'flex flex-col w-full justify-center md:justify-between flex-row! items-center gap-4! bg-semantic-bg-page!',
    subContainer:
      'flex items-center w-screen md:w-full! gap-1 md:mb-2 flex-col md:mb-0 md:gap-0 relative',
    pageName:
      'w-full font-semibold text-xl md:text-left lg:text-2xl md:pt-[10px] pb-1 md:pb-3 text-semantic-text-primary',
    titleWrapper: 'flex items-center w-full justify-between',
    pageDescription:
      'py-1 w-full bg-semantic-bg-page! md:px-0 md:pt-0 text-semantic-text-primary text-sm md:text-base',
    mobileFloatingHeader:
      'fixed top-2 left-3 right-3 z-[1350] bg-semantic-bg-surface border border-semantic-border px-3 pt-2 pb-2 flex items-center gap-2 rounded-semantic shadow-sm',
    mobileFloatingMenuButton:
      'flex items-center justify-center w-11 h-11 rounded-semantic text-semantic-text-secondary bg-transparent border-none shrink-0 active:bg-semantic-black/5',
    mobileFloatingTitleText:
      'flex-1 pl-1 text-xl font-bold text-semantic-text-primary truncate',
    mobileInlineHeader: 'flex items-center md:hidden h-14 px-4',
    mobileInlineMenuButton:
      'flex items-center justify-center rounded-semantic p-2 text-semantic-text-secondary bg-transparent border-none cursor-pointer hover:bg-semantic-bg-hover',
  },
};

interface GetContainerClassNameArgs {
  disableScroll: boolean;
  noPadding: boolean;
  hideScrollbar: boolean;
  containerClassName?: string;
  containerPaddingClassName: string;
  ownsScrollViewport: boolean;
}

function getContainerClassName({
  disableScroll,
  noPadding,
  hideScrollbar,
  containerClassName,
  containerPaddingClassName,
  ownsScrollViewport,
}: GetContainerClassNameArgs): string {
  const scroll =
    disableScroll || !ownsScrollViewport
      ? ''
      : stylesheet.containerScrollModifiers;
  const padding = noPadding ? '' : containerPaddingClassName;
  const scrollbar = hideScrollbar ? stylesheet.containerHideScrollbar : '';
  return [
    stylesheet.containerBase,
    scroll,
    padding,
    scrollbar,
    containerClassName,
  ]
    .filter(Boolean)
    .join(' ');
}

interface GetChildrenBoxClassNameArgs {
  isNoPaddingWithoutHeader: boolean;
  disableScroll: boolean;
  noChildrenSpacing: boolean;
}

function getChildrenBoxClassName({
  isNoPaddingWithoutHeader,
  disableScroll,
  noChildrenSpacing,
}: GetChildrenBoxClassNameArgs): string {
  if (!isNoPaddingWithoutHeader) {
    if (noChildrenSpacing) {
      return disableScroll ? stylesheet.childrenWrapperNoScrollNoSpacing : '';
    }
    return disableScroll
      ? stylesheet.childrenWrapperNoScroll
      : stylesheet.childrenWrapper;
  }

  if (disableScroll) {
    return stylesheet.childrenWrapperFullHeight;
  }

  return '';
}

function handleOpenSidebar(): void {
  useSidebarShellStore.getState().toggle();
}

interface DesktopHeaderProps {
  pageName?: string;
  pageDescription?: string;
  headerAction?: ReactNode;
}

function DesktopHeader({
  pageName,
  pageDescription,
  headerAction,
}: Readonly<DesktopHeaderProps>) {
  return (
    <div className={stylesheet.header.container}>
      <div className={stylesheet.header.subContainer}>
        <div className={stylesheet.header.titleWrapper}>
          <p className={stylesheet.header.pageName}>{pageName}</p>
        </div>
        {pageDescription && pageDescription.trim() !== '' ? (
          <p className={stylesheet.header.pageDescription}>{pageDescription}</p>
        ) : null}
      </div>
      {headerAction}
    </div>
  );
}

interface MobileFloatingHeaderProps {
  pageName?: string;
  mobileReturnPath?: string;
  mobileDescriptionSlot?: ReactNode;
  mobileTrailingSlot?: ReactNode;
}

function MobileFloatingHeader({
  pageName,
  mobileReturnPath,
  mobileDescriptionSlot,
  mobileTrailingSlot,
}: Readonly<MobileFloatingHeaderProps>) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <>
      <MobileTopGradient />
      <div className={stylesheet.header.mobileFloatingHeader}>
        {mobileReturnPath ? (
          <button
            type="button"
            className={stylesheet.header.mobileFloatingMenuButton}
            onClick={() => navigate(mobileReturnPath)}
            aria-label={t('common.return')}
          >
            <LuArrowLeft />
          </button>
        ) : (
          <button
            type="button"
            className={stylesheet.header.mobileFloatingMenuButton}
            onClick={handleOpenSidebar}
            aria-label={t('inbox.header.mobile.openMenu')}
          >
            <LuMenu />
          </button>
        )}
        <span className={stylesheet.header.mobileFloatingTitleText}>
          {pageName}
        </span>
        {mobileDescriptionSlot}
        {mobileTrailingSlot}
      </div>
    </>
  );
}

function MobileInlineHeader() {
  const { t } = useTranslation();

  return (
    <div className={stylesheet.header.mobileInlineHeader}>
      <button
        type="button"
        className={stylesheet.header.mobileInlineMenuButton}
        onClick={handleOpenSidebar}
        aria-label={t('aria-labels.openSidebar')}
      >
        <LuMenu />
      </button>
    </div>
  );
}

export function PageScaffold({
  children,
  pageName,
  pageDescription,
  returnPath,
  mobileReturnPath,
  mobileHeaderLayout = MobileHeaderLayout.Inline,
  noPadding = false,
  disableScroll = false,
  noChildrenSpacing = false,
  hideScrollbar = false,
  containerClassName,
  containerPaddingClassName = stylesheet.containerPaddingModifiers,
  ownsScrollViewport = true,
  mobileTopOffset = PAGE_SCAFFOLD_DEFAULT_MOBILE_TOP_OFFSET,
  headerAction,
  mobileDescriptionSlot,
  mobileTrailingSlot,
}: Readonly<PageScaffoldProps>) {
  const isMobile = useIsMobileViewport();
  const showHeader = Boolean(pageName) || Boolean(pageDescription?.trim());
  const isNoPaddingWithoutHeader = noPadding && !showHeader;
  const isFloatingLayout = mobileHeaderLayout === MobileHeaderLayout.Floating;

  const containerClass = getContainerClassName({
    disableScroll,
    noPadding,
    hideScrollbar,
    containerClassName,
    containerPaddingClassName,
    ownsScrollViewport,
  });
  const childrenBoxClassName = getChildrenBoxClassName({
    isNoPaddingWithoutHeader,
    disableScroll,
    noChildrenSpacing,
  });
  const containerStyle =
    !noPadding && isMobile && isFloatingLayout
      ? { marginTop: `${mobileTopOffset}px` }
      : undefined;

  const isFloatingMobileHeader = isFloatingLayout && isMobile;

  return (
    <>
      {mobileHeaderLayout === MobileHeaderLayout.Inline && (
        <MobileInlineHeader />
      )}
      <div
        className={
          disableScroll
            ? stylesheet.pageScaffoldWrapperNoScroll
            : stylesheet.pageScaffoldWrapper
        }
      >
        <div className={containerClass} style={containerStyle}>
          {showHeader && (
            <div className={stylesheet.showHeaderWrapper}>
              {returnPath && (!isMobile || !isFloatingLayout) && (
                <ReturnButton path={returnPath} />
              )}
              {isFloatingMobileHeader ? (
                <MobileFloatingHeader
                  pageName={pageName}
                  mobileReturnPath={mobileReturnPath}
                  mobileDescriptionSlot={mobileDescriptionSlot}
                  mobileTrailingSlot={mobileTrailingSlot}
                />
              ) : (
                <DesktopHeader
                  pageName={pageName}
                  pageDescription={pageDescription}
                  headerAction={headerAction}
                />
              )}
            </div>
          )}
          <div className={childrenBoxClassName}>{children}</div>
        </div>
      </div>
    </>
  );
}
